import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import consoleApi, { errorMessage } from "./consoleApi";
import { CARD, QUIET_AFTER_DAYS, StatusBadge, TenantMark, daysSince, formatDate, timeAgo } from "./format";
import { AlertIcon, ArrowRightIcon, BuildingIcon, PauseIcon, SearchIcon, ShieldIcon, UsersIcon } from "./icons";

const TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Waiting for review" },
  { key: "active", label: "Active" },
  { key: "suspended", label: "Suspended" },
];

function Stat({ icon: IconComponent, label, value, tint }) {
  return (
    <div className={`${CARD} flex items-center gap-4 px-5 py-4`}>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tint}`}>
        <IconComponent className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <p className="text-2xl font-bold tabular-nums leading-none text-slate-900">{value ?? "—"}</p>
        <p className="mt-1 truncate text-xs font-medium text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function LastActivity({ tenant }) {
  const days = daysSince(tenant.lastActivityAt);
  const quiet = tenant.status === "active" && (days === null || days >= QUIET_AFTER_DAYS);
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap ${quiet ? "font-medium text-amber-700" : "text-slate-600"}`}>
      {quiet && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title={`No activity for ${QUIET_AFTER_DAYS}+ days`} />}
      {timeAgo(tenant.lastActivityAt)}
    </span>
  );
}

export default function TenantList() {
  const navigate = useNavigate();
  const { overview } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const tab = params.get("status") || "all";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    consoleApi.get("/tenants", { params: { status: tab } })
      .then((res) => { if (!cancelled) setTenants(res.data.data); })
      .catch((err) => { if (!cancelled) setError(errorMessage(err, "Couldn't load tenants.")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((t) =>
      [t.companyName, t.domain, ...(t.domains || []), t.contact?.email, t.contact?.firstName, t.contact?.lastName]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [tenants, search]);

  const counts = overview?.tenants || {};
  const pending = counts.pending || 0;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Tenants</h1>
        <p className="mt-1 text-sm text-slate-500">Every organisation on RiskFlow, how much they use it, and who's waiting to get in.</p>
      </div>

      {pending > 0 && tab !== "pending" && (
        <button
          onClick={() => setParams({ status: "pending" })}
          className="group flex w-full items-center gap-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-4 text-left transition hover:border-amber-300"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400 text-amber-950">
            <AlertIcon className="h-5 w-5" />
          </span>
          <span className="flex-1">
            <span className="block text-sm font-bold text-amber-950">
              {pending} signup{pending === 1 ? "" : "s"} waiting for review
            </span>
            <span className="block text-sm text-amber-800">Nobody at {pending === 1 ? "this organisation" : "these organisations"} can sign in until you approve.</span>
          </span>
          <span className="hidden items-center gap-1 text-sm font-semibold text-amber-900 sm:flex">
            Review <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </span>
        </button>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={ShieldIcon} label="Active tenants" value={counts.active} tint="bg-emerald-50 text-emerald-600" />
        <Stat icon={PauseIcon} label="Suspended" value={counts.suspended} tint="bg-slate-100 text-slate-500" />
        <Stat icon={UsersIcon} label="Users, all tenants" value={overview?.users} tint="bg-indigo-50 text-indigo-600" />
        <Stat icon={BuildingIcon} label="Risks, all tenants" value={overview?.risks} tint="bg-violet-50 text-violet-600" />
      </div>

      <div className={CARD}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div className="flex flex-wrap gap-1" role="tablist" aria-label="Filter by status">
            {TABS.map((t) => {
              const n = t.key === "all" ? null : counts[t.key];
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setParams(t.key === "all" ? {} : { status: t.key })}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                    active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  {t.label}
                  {n > 0 && (
                    <span className={`rounded-full px-1.5 text-[11px] font-bold tabular-nums ${
                      active ? "bg-white/20 text-white" : t.key === "pending" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"
                    }`}>
                      {n}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <label className="relative w-full sm:w-72">
            <span className="sr-only">Search tenants</span>
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, domain or contact"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm placeholder:text-slate-400 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </label>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="space-y-3 p-5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-9 w-9 animate-pulse rounded-lg bg-slate-100" />
                  <div className="h-4 flex-1 animate-pulse rounded bg-slate-100" />
                </div>
              ))}
            </div>
          ) : error ? (
            <p className="p-6 text-sm text-red-600">{error}</p>
          ) : visible.length === 0 ? (
            <div className="px-6 py-14 text-center">
              <BuildingIcon className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-700">
                {search ? "No tenants match your search" : tab === "pending" ? "All caught up" : "No tenants here"}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                {search ? "Try a name, domain or email." : tab === "pending" ? "New signups will appear here." : ""}
              </p>
            </div>
          ) : (
            <table className="min-w-full text-left text-sm">
              <thead className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-5 py-3">Organisation</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3 text-right">Users</th>
                  <th className="px-4 py-3 text-right">Risks</th>
                  <th className="px-4 py-3">Last activity</th>
                  <th className="px-5 py-3">Signed up</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((t) => (
                  <tr key={t.id} onClick={() => navigate(`/tenants/${t.id}`)} className="cursor-pointer transition-colors hover:bg-indigo-50/40">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <TenantMark tenant={t} />
                        <div className="min-w-0">
                          <Link
                            to={`/tenants/${t.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="block truncate font-semibold text-slate-900 hover:text-indigo-700"
                          >
                            {t.companyName}
                          </Link>
                          <p className="truncate text-xs text-slate-500">{t.domain}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5"><StatusBadge status={t.status} /></td>
                    <td className="px-4 py-3.5">
                      {t.contact ? (
                        <>
                          <p className="whitespace-nowrap text-slate-800">{t.contact.firstName} {t.contact.lastName}</p>
                          <p className="text-xs text-slate-500">{t.contact.email}</p>
                        </>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-right font-medium tabular-nums text-slate-800">{t.userCount}</td>
                    <td className="px-4 py-3.5 text-right font-medium tabular-nums text-slate-800">{t.riskCount}</td>
                    <td className="px-4 py-3.5"><LastActivity tenant={t} /></td>
                    <td className="whitespace-nowrap px-5 py-3.5 text-slate-500">{formatDate(t.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
